import Chart from 'chart.js/auto'
import { toFiniteNumber, toLocalDateKey } from './analyticsData'

export function destroyCharts(chartInstances) {
  for (const chart of chartInstances.splice(0)) {
    chart.destroy()
  }
}

export function renderCharts(chartsContainer, chartInstances, surveyData, responsesData = []) {
  if (!chartsContainer) return

  destroyCharts(chartInstances)
  chartsContainer.innerHTML = ''

  const colorPalettes = {
    pie: ['#0D9488', '#0EA5E9', '#48bb78', '#ed8936', '#e53e3e', '#38b2ac', '#9f7aea'],
    bar: ['#0D9488', '#0EA5E9', '#48bb78', '#ed8936', '#e53e3e', '#38b2ac', '#9f7aea'],
  }

  // Get chartable questions
  const definition = surveyData?.definition
  if (!definition) return

  const chartableTypes = ['select', 'radio', 'checkbox', 'rating']
  const questions = definition.questions || []
  const chartableQuestions = questions.filter((q) => chartableTypes.includes(q.type))
  const ratingQuestions = questions.filter((q) => q.type === 'rating')

  if (chartableQuestions.length === 0 && ratingQuestions.length === 0) {
    chartsContainer.innerHTML =
      '<div class="no-charts"><p>No chartable questions in this survey.</p></div>'
    return
  }

  chartableQuestions.forEach((question) => {
    const valueCounts = {}

    responsesData.forEach((r) => {
      const answer = r.answers?.[question.id]
      if (answer === undefined || answer === null || answer === '') return

      if (question.type === 'rating') {
        const rating = toFiniteNumber(answer)
        if (rating === null) return
        valueCounts[rating] = (valueCounts[rating] || 0) + 1
      } else if (Array.isArray(answer)) {
        answer.forEach((val) => {
          valueCounts[val] = (valueCounts[val] || 0) + 1
        })
      } else {
        valueCounts[String(answer)] = (valueCounts[String(answer)] || 0) + 1
      }
    })

    if (Object.keys(valueCounts).length === 0) return

    createChartCard(
      chartsContainer,
      chartInstances,
      question.label || question.id,
      valueCounts,
      question.type === 'radio' || question.type === 'select' ? 'pie' : 'bar',
      colorPalettes
    )
  })

  // Responses Over Time — line chart
  const dateCounts = {}
  responsesData.forEach((r) => {
    if (!r.submitted_at) return
    const key = toLocalDateKey(r.submitted_at)
    if (!key) return
    dateCounts[key] = (dateCounts[key] || 0) + 1
  })

  const sortedDates = Object.keys(dateCounts).sort()
  if (sortedDates.length > 0) {
    const labels = sortedDates.map((iso) =>
      new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    )
    const counts = sortedDates.map((iso) => dateCounts[iso])

    const wrap = document.createElement('div')
    wrap.className = 'chart-card'
    const header = document.createElement('div')
    header.className = 'chart-header'
    header.textContent = 'Responses Over Time'
    const chartContainer = document.createElement('div')
    chartContainer.className = 'chart-container'
    const canvas = document.createElement('canvas')
    chartContainer.appendChild(canvas)
    wrap.appendChild(header)
    wrap.appendChild(chartContainer)
    chartsContainer.appendChild(wrap)

    const instance = new Chart(canvas.getContext('2d'), {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Responses',
            data: counts,
            borderColor: '#0D9488',
            backgroundColor: 'rgba(13, 148, 136, 0.1)',
            fill: false,
            tension: 0.2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#0D9488',
            pointBorderColor: '#0D9488',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tipCtx) => `${tipCtx.parsed.y} response${tipCtx.parsed.y !== 1 ? 's' : ''}`,
            },
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { color: 'rgba(0,0,0,0.05)' },
            title: { display: true, text: 'Responses', font: { size: 11 } },
          },
        },
        animation: { duration: 800, easing: 'easeOutQuart' },
      },
    })
    chartInstances.push(instance)
  }

  // Rating-specific charts — only rendered for surveys with rating questions
  ratingQuestions.forEach((question) => {
    const ratingData = []
    responsesData.forEach((r) => {
      const answer = r.answers?.[question.id]
      if (answer === undefined || answer === null || answer === '') return
      const val = toFiniteNumber(answer)
      if (val !== null) ratingData.push({ val, submitted_at: r.submitted_at })
    })

    if (ratingData.length === 0) return

    const ratingCounts = {}
    ratingData.forEach(({ val }) => {
      ratingCounts[val] = (ratingCounts[val] || 0) + 1
    })
    const ratingValues = Object.keys(ratingCounts)
      .map(Number)
      .sort((a, b) => a - b)
    const ratingCountsArr = ratingValues.map((v) => ratingCounts[v])
    const maxCount = Math.max(...ratingCountsArr)

    // Rating Distribution — bubble chart (point size = relative frequency)
    {
      const wrap = document.createElement('div')
      wrap.className = 'chart-card'
      const header = document.createElement('div')
      header.className = 'chart-header'
      header.textContent = `${question.label || question.id} — Rating Distribution`
      const chartContainer = document.createElement('div')
      chartContainer.className = 'chart-container'
      const canvas = document.createElement('canvas')
      chartContainer.appendChild(canvas)
      wrap.appendChild(header)
      wrap.appendChild(chartContainer)
      chartsContainer.appendChild(wrap)

      const instance = new Chart(canvas.getContext('2d'), {
        type: 'bubble',
        data: {
          datasets: [
            {
              label: 'Responses',
              data: ratingValues.map((v, i) => ({
                x: v,
                y: ratingCountsArr[i],
                r: 4 + (ratingCountsArr[i] / maxCount) * 8,
              })),
              backgroundColor: 'rgba(13, 148, 136, 0.5)',
              borderColor: '#0D9488',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (tipCtx) =>
                  `Rating ${tipCtx.raw.x}: ${tipCtx.raw.y} response${tipCtx.raw.y !== 1 ? 's' : ''}`,
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: 'Rating', font: { size: 11 } },
              ticks: { stepSize: 1, precision: 0 },
              grid: { display: false },
              offset: true,
              suggestedMin: Math.min(...ratingValues) - 0.5,
              suggestedMax: Math.max(...ratingValues) + 0.5,
            },
            y: {
              beginAtZero: true,
              ticks: { precision: 0 },
              grid: { color: 'rgba(0,0,0,0.05)' },
              title: { display: true, text: 'Count', font: { size: 11 } },
            },
          },
          animation: { duration: 600, easing: 'easeOutQuart' },
        },
      })
      chartInstances.push(instance)
    }

    // Rating Trend Over Time — line chart showing avg rating per day
    const dateRatings = {}
    ratingData.forEach(({ val, submitted_at }) => {
      if (!submitted_at) return
      const key = toLocalDateKey(submitted_at)
      if (!key) return
      if (!dateRatings[key]) dateRatings[key] = []
      dateRatings[key].push(val)
    })

    const trendDates = Object.keys(dateRatings).sort()
    if (trendDates.length >= 2) {
      const trendLabels = trendDates.map((iso) =>
        new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      )
      const avgRatings = trendDates.map((iso) => {
        const vals = dateRatings[iso]
        return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)
      })

      const wrap = document.createElement('div')
      wrap.className = 'chart-card'
      const header = document.createElement('div')
      header.className = 'chart-header'
      header.textContent = `${question.label || question.id} — Rating Trend`
      const chartContainer = document.createElement('div')
      chartContainer.className = 'chart-container'
      const canvas = document.createElement('canvas')
      chartContainer.appendChild(canvas)
      wrap.appendChild(header)
      wrap.appendChild(chartContainer)
      chartsContainer.appendChild(wrap)

      const instance = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: trendLabels,
          datasets: [
            {
              label: 'Avg Rating',
              data: avgRatings,
              borderColor: '#0EA5E9',
              backgroundColor: 'rgba(14, 165, 233, 0.1)',
              fill: false,
              tension: 0.2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#0EA5E9',
              pointBorderColor: '#0EA5E9',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (tipCtx) => `Avg: ${tipCtx.parsed.y}`,
              },
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: {
              ticks: { precision: 1 },
              grid: { color: 'rgba(0,0,0,0.05)' },
              title: { display: true, text: 'Average Rating', font: { size: 11 } },
            },
          },
          animation: { duration: 600, easing: 'easeOutQuart' },
        },
      })
      chartInstances.push(instance)
    }
  })
}

function createChartCard(
  chartsContainer,
  chartInstances,
  title,
  valueCounts,
  chartType,
  colorPalettes
) {
  if (!chartsContainer) return

  const wrap = document.createElement('div')
  wrap.className = 'chart-card'

  const header = document.createElement('div')
  header.className = 'chart-header'
  header.textContent = title

  const chartContainer = document.createElement('div')
  chartContainer.className = 'chart-container'

  const canvas = document.createElement('canvas')

  chartContainer.appendChild(canvas)
  wrap.appendChild(header)
  wrap.appendChild(chartContainer)
  chartsContainer.appendChild(wrap)

  const labels = Object.keys(valueCounts)
  const data = Object.values(valueCounts)
  const colors = colorPalettes[chartType]

  const chartConfig = {
    type: chartType === 'pie' ? 'doughnut' : 'bar',
    data: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, labels.length),
          borderWidth: 0,
          borderRadius: chartType === 'bar' ? 6 : 0,
          maxBarThickness: 40,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 0,
      plugins: {
        legend: {
          display: chartType === 'pie',
          position: 'bottom',
          labels: { padding: 12, usePointStyle: true, font: { size: 11 } },
        },
      },
      scales:
        chartType === 'bar'
          ? {
              y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
              x: { grid: { display: false } },
            }
          : {},
      animation: { duration: 600, easing: 'easeOutQuart' },
    },
  }

  const instance = new Chart(canvas.getContext('2d'), chartConfig)
  chartInstances.push(instance)
}
